import { SupplierDocument, SUPPLIER_DOCUMENT_TYPE_OPTIONS } from 'apis/supplier-documents';
import dayjs from 'dayjs';

export type DocumentExpiryStatus = 'expired' | 'expiring' | 'valid' | 'no_expiry';

/** A licence renewed only after it lapses is the failure mode this exists to catch, so
 * "expiring" opens a month ahead — long enough for ops to chase the supplier. */
export const EXPIRING_SOON_DAYS = 30;

/** Derived client-side: the BE stores the date and nothing else, so there is no status to
 * go stale in the database as time passes. `expiryDate` is DATEONLY, hence day granularity —
 * a document expiring today still counts as valid for the whole of today. */
export const getDocumentExpiryStatus = (expiryDate: string | null, now = dayjs()): DocumentExpiryStatus => {
  if (!expiryDate) return 'no_expiry';

  const daysLeft = dayjs(expiryDate).startOf('day').diff(now.startOf('day'), 'day');
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= EXPIRING_SOON_DAYS) return 'expiring';
  return 'valid';
};

/** Same hex + rgba(…, 0.08) palette as the restaurant document vault's status badge, so the
 * two document surfaces read as one system. */
export const DOCUMENT_EXPIRY_META: Record<DocumentExpiryStatus, { label: string; color: string; bg: string }> = {
  expired: { label: 'Expired', color: '#E53E3E', bg: 'rgba(229, 62, 62, 0.08)' },
  expiring: { label: 'Expiring soon', color: '#D69E2E', bg: 'rgba(214, 158, 46, 0.08)' },
  valid: { label: 'Valid', color: '#38A169', bg: 'rgba(56, 161, 105, 0.08)' },
  no_expiry: { label: 'No expiry', color: '#718096', bg: 'rgba(113, 128, 150, 0.08)' },
};

/** What ops actually called the document: the free-text label for OTHER, the type's name
 * otherwise. Falls back to the raw type so an enum value added by the BE ahead of the FE
 * still renders something recognisable rather than a blank cell. */
export const getDocumentLabel = (document: Pick<SupplierDocument, 'type' | 'customLabel'>): string => {
  if (document.type === 'OTHER') return document.customLabel?.trim() || 'Other';
  return SUPPLIER_DOCUMENT_TYPE_OPTIONS.find((option) => option.value === document.type)?.label ?? document.type;
};
